import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Paperclip, Link2, Trash2, Loader2, Upload, FileText } from "lucide-react";

const fmtSize = (n) => (n ? (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`) : "");

async function uploadFile(file, purpose) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/admin/uploads/file?purpose=${purpose}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export function AttachmentsField({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState({ title: "", url: "" });

  const onFiles = async (files) => {
    setBusy(true);
    const added = [];
    for (const f of files) {
      try {
        const r = await uploadFile(f, "lesson");
        added.push({ title: f.name.replace(/\.[^.]+$/, ""), url: r.url, kind: "file", filename: r.filename, size: r.size, content_type: r.content_type });
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    if (added.length) onChange([...items, ...added]);
    setBusy(false);
  };

  const addLink = () => {
    if (!link.url.trim()) return;
    onChange([...items, { title: link.title.trim() || link.url.trim(), url: link.url.trim(), kind: "link" }]);
    setLink({ title: "", url: "" });
  };

  const patch = (i, p) => onChange(items.map((it, j) => (j === i ? { ...it, ...p } : it)));

  return (
    <div data-testid="admin-lesson-attachments" className="mt-1 space-y-2 rounded-lg border border-slate-200 p-3 bg-slate-50/60">
      {items.map((it, i) => (
        <div key={`${it.url}-${i}`} className="flex items-center gap-2" data-testid={`admin-attachment-${i}`}>
          {it.kind === "link" ? <Link2 size={14} className="text-slate-400 shrink-0" /> : <Paperclip size={14} className="text-slate-400 shrink-0" />}
          <Input value={it.title} onChange={(e) => patch(i, { title: e.target.value })} className="h-8 text-sm" data-testid={`admin-attachment-title-${i}`} />
          <span className="text-[11px] text-slate-400 whitespace-nowrap">{it.kind === "link" ? "link" : fmtSize(it.size)}</span>
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600 p-1" aria-label="Remove attachment" data-testid={`admin-attachment-remove-${i}`}>
            <Trash2 size={13} />
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg" multiple hidden
          onChange={(e) => { onFiles([...e.target.files]); e.target.value = ""; }} data-testid="admin-attachment-file-input" />
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="h-8 gap-1.5" data-testid="admin-attachment-upload">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload PDF / JPG
        </Button>
        <span className="text-[11px] text-slate-400">or paste a link:</span>
        <Input value={link.title} onChange={(e) => setLink((l) => ({ ...l, title: e.target.value }))} placeholder="Title" className="h-8 w-36 text-sm" data-testid="admin-attachment-link-title" />
        <Input value={link.url} onChange={(e) => setLink((l) => ({ ...l, url: e.target.value }))} placeholder="https://drive.google.com/…" className="h-8 flex-1 min-w-[160px] text-sm" data-testid="admin-attachment-link-url" />
        <Button type="button" size="sm" variant="outline" onClick={addLink} className="h-8" data-testid="admin-attachment-add-link">Add link</Button>
      </div>
    </div>
  );
}

export function FileUploadField({ value, onChange, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (f) => {
    setBusy(true);
    try {
      const r = await uploadFile(f, "download");
      onUploaded(r);
      toast.success(`Uploaded ${r.filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-1 flex items-center gap-2">
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste a link, or upload a file →" className="flex-1" data-testid="admin-resource-file-url" />
      <input ref={inputRef} type="file" hidden onChange={(e) => { if (e.target.files[0]) onFile(e.target.files[0]); e.target.value = ""; }}
        accept=".pdf,.jpg,.jpeg,.png,.tpl,.set,.ex4,.ex5,.mq4,.mq5,.json,.txt,.csv,.xlsx,.zip,.pine" data-testid="admin-resource-file-input" />
      <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="gap-1.5 shrink-0" data-testid="admin-resource-upload">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Upload file
      </Button>
    </div>
  );
}
